import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { motion } from "framer-motion";

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08, delayChildren: 0.1 } },
};
const rise = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

export default function Home() {
  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative">
        <div className="container-x pt-16 md:pt-28 pb-24 md:pb-40">
          <motion.div initial="hidden" animate="show" variants={stagger}>
            <motion.div variants={rise} className="flex items-center gap-3 mb-10">
              <span className="inline-block w-8 h-px bg-foreground" />
              <span className="eyebrow">Strategic Investment · Operating Firm</span>
            </motion.div>

            <motion.h1
              variants={rise}
              className="hero-headline text-[44px] sm:text-6xl md:text-7xl lg:text-[96px] max-w-[19ch]"
              data-testid="hero-headline"
            >
              A strategic investment <span className="text-muted-foreground">and</span> operating firm focused on commerce, media, <span className="text-muted-foreground">and</span> emerging distribution ecosystems.
            </motion.h1>

            <motion.p
              variants={rise}
              className="mt-10 max-w-2xl text-base md:text-lg font-light leading-relaxed text-foreground/80"
              data-testid="hero-subheading"
            >
              SamStella is an operator-led platform built for founders, brands and platforms scaling at the intersection of audience, monetization, and distribution. Revenue-first execution. Cross-platform expertise. Speed and agility by design.
            </motion.p>

            <motion.div variants={rise} className="mt-12 flex flex-wrap items-center gap-4">
              <Link to="/contact" className="btn-primary" data-testid="hero-cta-getintouch">
                Get in Touch <ArrowRight size={14} />
              </Link>
              <Link to="/portfolio" className="btn-secondary" data-testid="hero-cta-portfolio">
                View Portfolio <ArrowUpRight size={14} />
              </Link>
            </motion.div>
          </motion.div>
        </div>

        <div className="border-t border-border">
          <div className="container-x py-8 grid grid-cols-2 md:grid-cols-4 gap-y-6 gap-x-10">
            {[
              { k: "20+", v: "Years operating" },
              { k: "13+", v: "Portfolio companies" },
              { k: "$2B+", v: "Enterprise value built" },
              { k: "NYSE", v: "Tremor Video IPO" },
            ].map((s) => (
              <div key={s.v} data-testid={`hero-stat-${s.v.replace(/\s+/g, "-").toLowerCase()}`}>
                <div className="font-display text-3xl md:text-4xl tracking-tight">{s.k}</div>
                <div className="font-mono-label text-[10px] tracking-[0.22em] uppercase text-muted-foreground mt-2">
                  {s.v}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="border-t border-border">
        <div className="container-x py-24 md:py-40 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <p className="eyebrow">Manifesto · 01</p>
          </div>
          <div className="md:col-span-8">
            <p
              className="font-display text-3xl md:text-5xl tracking-tight leading-[1.1]"
              data-testid="home-manifesto"
            >
              “Great businesses do not fail because of lack of vision — they fail because execution, distribution, monetization, and timing rarely align at the same moment.”
            </p>
            <p className="mt-10 text-base md:text-lg font-light leading-relaxed max-w-2xl text-foreground/80">
              SamStella was built to close that gap. We combine capital, operational support, strategic relationships, and go-to-market execution under one umbrella — moving faster than traditional holding companies.
            </p>
          </div>
        </div>
      </section>

      {/* ABOUT INTRO */}
      <section className="border-t border-border bg-secondary/40">
        <div className="container-x py-24 md:py-32">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
            <div>
              <p className="eyebrow mb-4">Focus · 02</p>
              <h2 className="font-display text-3xl md:text-5xl tracking-tight leading-tight max-w-[18ch]">
                Where attention is moving, and how it monetizes.
              </h2>
            </div>
            <Link to="/about" className="font-mono-label text-[11px] tracking-[0.22em] uppercase link-underline" data-testid="home-link-about">
              Read the philosophy →
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 border-t border-border pt-14">
            <div className="md:col-span-7 space-y-6 text-base md:text-lg font-light leading-relaxed text-foreground/85" data-testid="home-about-intro">
              <p>
                SamStella is a strategic investment, incubation and business development firm focused on scaling modern commerce, media, advertising and technology businesses. Founded by Andrew S. Reis, we operate as both an ownership platform and an execution engine — identifying high-growth opportunities, building operational infrastructure, and driving revenue acceleration at every stage.
              </p>
              <p>
                We sit at the intersection of commerce + media, advertising + attribution, live shopping + distribution, the creator economy, and retail's emerging platforms. Unlike traditional investment firms, we operate inside the businesses we support — partnering with founders, platforms, brands and operators to commercialize the next generation of consumer engagement.
              </p>

              <div className="pt-10 mt-4 border-t border-border" data-testid="home-about-platforms">
                <p className="eyebrow mb-5">Active across</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3">
                  {[
                    "TikTok Shop",
                    "Whatnot",
                    "Amazon Live",
                    "eBay Live",
                    "Palmstreet",
                    "Shopify",
                    "Connected TV",
                    "Retail Media",
                    "Creator Economy",
                  ].map((p) => (
                    <div
                      key={p}
                      className="font-mono-label text-[11px] tracking-[0.2em] uppercase text-foreground/80 flex items-center gap-2"
                    >
                      <span className="inline-block w-1 h-1 bg-foreground" />
                      {p}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="md:col-span-5">
              <div
                className="relative aspect-[4/5] w-full overflow-hidden border border-border bg-background"
                data-testid="home-about-image"
              >
                <img
                  src="https://customer-assets.emergentagent.com/job_distrib-tech/artifacts/zhv5z4b1_pexels-ruining-song-3594016-29422663.jpg"
                  alt="SamStella operating office — view onto the city"
                  className="absolute inset-0 w-full h-full object-cover grayscale contrast-[1.05] transition-transform duration-700 ease-out hover:scale-[1.03]"
                />
                <span className="absolute bottom-4 left-4 font-mono-label text-[10px] tracking-[0.22em] uppercase text-background mix-blend-difference">
                  SamStella · est. operating platform
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA BAND */}
      <section className="border-t border-border bg-foreground text-background">
        <div className="container-x py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-8">
            <p className="font-mono-label text-[11px] tracking-[0.22em] uppercase text-background/60 mb-6">
              Partner with SamStella
            </p>
            <h2 className="font-display text-3xl md:text-5xl tracking-tight leading-[1.05]">
              Capital, infrastructure, and operating leverage — under one umbrella.
            </h2>
          </div>
          <div className="md:col-span-4 md:justify-self-end">
            <Link
              to="/contact"
              data-testid="home-cta-band-contact"
              className="inline-flex items-center gap-3 px-6 py-4 bg-background text-foreground font-mono-label text-[11px] tracking-[0.22em] uppercase hover:bg-accent hover:text-background transition-colors"
            >
              Start a conversation <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
